import { useState, useMemo, useEffect } from "react";
import { api, downloadFile, API } from "@/lib/api";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { AlertTriangle, ScanLine, Check, X, Pencil, ExternalLink, RefreshCw, Eye } from "lucide-react";

// --- Small helpers --------------------------------------------------------

const confBucket = (c) => {
  const n = Number(c || 0);
  if (n >= 0.85) return { label: "auto", color: "bg-emerald-100 text-emerald-800 border-emerald-300" };
  if (n >= 0.5)  return { label: "review", color: "bg-amber-100 text-amber-900 border-amber-300" };
  return { label: "manual", color: "bg-rose-100 text-rose-900 border-rose-300" };
};

function ConfChip({ value, viaOcr }) {
  const b = confBucket(value);
  return (
    <span className={`inline-flex items-center gap-1 border rounded px-1.5 py-0.5 text-[10px] font-semibold ${b.color}`} data-testid={`conf-chip-${b.label}`}>
      {viaOcr ? <ScanLine className="w-3 h-3" /> : null}
      {(Number(value || 0) * 100).toFixed(0)}%
      <span className="opacity-70">· {b.label}</span>
      {viaOcr ? <span className="opacity-70">· OCR</span> : null}
    </span>
  );
}

function SourceRef({ ref }) {
  if (!ref) return null;
  const bits = [];
  if (ref.page) bits.push(`p.${ref.page}`);
  if (ref.sheet) bits.push(`sheet:${ref.sheet}`);
  if (ref.row) bits.push(`row:${ref.row}`);
  if (ref.line_index != null) bits.push(`line:${ref.line_index}`);
  if (ref.via_ocr) bits.push("OCR");
  if (ref.ocr_avg_conf_pct != null) bits.push(`ocr:${ref.ocr_avg_conf_pct}%`);
  return <span className="text-[10px] text-slate-500" data-testid="source-ref">{bits.join(" · ")}</span>;
}

function ReviewControls({ path, review, onReview, canReview, canEdit }) {
  const state = review?.action;
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState(review?.note || "");
  const [newVal, setNewVal] = useState(review?.new_value ?? "");
  const submit = async (action) => {
    try {
      await onReview({ field_path: path, action, note, new_value: action === "edit" ? newVal : undefined });
      setEditing(false);
    } catch (e) { toast.error(e.message || "Review failed"); }
  };
  if (!canReview) {
    return state ? <span className="text-[10px] text-slate-500 mt-1">{state} · {review?.reviewed_at?.slice(0,16).replace('T',' ')}</span> : null;
  }
  return (
    <div className="flex flex-col gap-1 mt-1">
      <div className="flex gap-1">
        <Button size="sm" variant={state === "accept" ? "default" : "outline"} onClick={() => submit("accept")} data-testid={`accept-${path}`}>
          <Check className="w-3 h-3 mr-1" />Accept
        </Button>
        {canEdit && (
          <Button size="sm" variant={state === "edit" ? "default" : "outline"} onClick={() => setEditing(v => !v)} data-testid={`edit-${path}`}>
            <Pencil className="w-3 h-3 mr-1" />Edit
          </Button>
        )}
        <Button size="sm" variant={state === "reject" ? "destructive" : "outline"} onClick={() => submit("reject")} data-testid={`reject-${path}`}>
          <X className="w-3 h-3 mr-1" />Reject
        </Button>
        {state ? <span className="text-[10px] text-slate-500 self-center">{state} · {review?.reviewed_at?.slice(0,16).replace('T',' ')}</span> : null}
      </div>
      {editing && canEdit ? (
        <div className="flex gap-1 items-center">
          <Input value={newVal} onChange={(e) => setNewVal(e.target.value)} className="h-7 text-xs" placeholder="Corrected value" data-testid={`edit-val-${path}`} />
          <Input value={note} onChange={(e) => setNote(e.target.value)} className="h-7 text-xs" placeholder="Reason (optional)" />
          <Button size="sm" onClick={() => submit("edit")}>Save</Button>
        </div>
      ) : null}
    </div>
  );
}

// --- Main tab -------------------------------------------------------------

export default function SupplierDocumentTab({ rfq, onRefresh }) {
  const { can, loaded: permsLoaded } = usePermissions();
  const canExtract = permsLoaded && can("spec.extract");
  const canReview = permsLoaded && can("spec.review");
  const canReviewEdit = permsLoaded && can("spec.review.edit");
  const canGenerateDoc = permsLoaded && can("spec.generate");
  const canReadBuyerOriginals = permsLoaded && can("rfq.buyer_originals.read");

  const spec = rfq?.supplier_spec || null;
  const ex = spec?.extraction || null;
  const reviews = spec?.reviews || {};
  const stale = !!rfq?.sources_changed_since_extraction;
  const staleIds = rfq?.stale_source_file_ids || [];
  const [previewFileId, setPreviewFileId] = useState(rfq?.documents?.[0]?.id || null);
  const [selectedSources, setSelectedSources] = useState((rfq?.documents || []).map(d => d.id));
  const [running, setRunning] = useState(false);

  const buyerDocs = rfq?.documents || [];   // admin view exposes hydrated buyer documents

  const runExtract = async () => {
    if (!selectedSources.length) { toast.error("Pick at least one source file"); return; }
    setRunning(true);
    try {
      const r = await api(`/rfqs/${rfq.id}/extract-supplier-spec`, { method: "POST", body: { source_file_ids: selectedSources } });
      toast.success(`Extracted v${r.supplier_spec_version} — ${r.line_items_count} line items, ${r.warnings_count} warnings, ${r.unresolved_count} unresolved`);
      onRefresh && onRefresh();
    } catch (e) { toast.error(e.message || "Extraction failed"); }
    finally { setRunning(false); }
  };

  const postReview = async (payload) => {
    await api(`/rfqs/${rfq.id}/supplier-spec/review`, { method: "POST", body: payload });
    onRefresh && onRefresh();
  };

  const previewFileName = useMemo(() => {
    if (!previewFileId) return "";
    const d = buyerDocs.find(x => x.id === previewFileId);
    return d?.original_filename || "";
  }, [previewFileId, buyerDocs]);

  // Fetch protected file via the authenticated axios instance and stream
  // it into a browser Blob URL. This keeps the JWT off of the iframe src,
  // out of window.location, out of browser history, out of the Referer
  // header on any nested request the iframe might issue, and out of any
  // access log that records query strings. Cleanup revokes the URL to
  // free memory when the user switches file / leaves the tab.
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  useEffect(() => {
    if (!previewFileId) { setPreviewBlobUrl(null); setPreviewError(null); return; }
    let cancelled = false;
    let created = null;
    setPreviewLoading(true);
    setPreviewError(null);
    (async () => {
      try {
        const resp = await api.get(`/files/${previewFileId}/download`, { responseType: "blob" });
        if (cancelled) return;
        created = URL.createObjectURL(resp.data);
        setPreviewBlobUrl(created);
      } catch (e) {
        if (!cancelled) setPreviewError(e?.response?.data?.detail || "Unable to load preview");
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
      setPreviewBlobUrl(null);
    };
  }, [previewFileId]);

  const requiredKeys = useMemo(() => (ex?.unresolved_fields || []), [ex]);
  const allResolved = requiredKeys.every(k => ["accept", "edit"].includes(reviews[k]?.action));
  const canGenerate = spec?.product_title && (!requiredKeys.length || allResolved);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="supplier-document-tab">
      {/* Left: original document + controls */}
      <div className="space-y-3">
        <Card className="p-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">Buyer originals (private)</h3>
            <span className="text-[10px] text-slate-500">never shared with suppliers</span>
          </div>
          {buyerDocs.length === 0 ? <p className="text-xs text-slate-500 mt-2">No buyer documents attached.</p> : (
            <ul className="mt-2 space-y-1" data-testid="buyer-docs-list">
              {buyerDocs.map(d => (
                <li key={d.id} className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={selectedSources.includes(d.id)} onChange={(e) => setSelectedSources(prev => e.target.checked ? [...prev, d.id] : prev.filter(x => x !== d.id))} data-testid={`src-${d.id}`} />
                  <button className="underline hover:text-emerald-700 flex-1 text-left truncate" onClick={() => setPreviewFileId(d.id)}>
                    <Eye className="w-3 h-3 inline mr-1" />{d.original_filename}
                  </button>
                  {staleIds.includes(d.id) ? <span className="text-[10px] text-rose-700 font-semibold">STALE</span> : null}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-2 flex gap-2">
            <Button size="sm" onClick={runExtract} disabled={running || !selectedSources.length || !canExtract} data-testid="run-extract">
              <RefreshCw className={`w-3 h-3 mr-1 ${running ? "animate-spin" : ""}`} />
              {ex ? "Re-extract from selected" : "Extract from selected"}
            </Button>
            <span className="text-[10px] text-slate-500 self-center">deterministic + OCR · no AI</span>
            {!canExtract && <span className="text-[10px] text-slate-500 self-center">· read-only (no spec.extract)</span>}
          </div>
        </Card>

        <Card className="p-3">
          <h3 className="font-semibold text-sm mb-2">Original preview</h3>
          {previewFileId ? (
            previewLoading ? (
              <p className="text-xs text-slate-500">Loading preview…</p>
            ) : previewError ? (
              <p className="text-xs text-rose-700" data-testid="preview-error">{String(previewError)}</p>
            ) : previewBlobUrl ? (
              <>
                <iframe title="Original document" src={previewBlobUrl} className="w-full h-[560px] border rounded" data-testid="original-preview-frame" />
                <p className="text-[10px] text-slate-400 mt-1">Streamed via authenticated blob — no token in URL.</p>
              </>
            ) : (
              <p className="text-xs text-slate-500">Preparing preview…</p>
            )
          ) : <p className="text-xs text-slate-500">Select a file above to preview.</p>}
        </Card>
      </div>

      {/* Right: extracted spec + review controls */}
      <div className="space-y-3">
        {stale ? (
          <div className="bg-rose-50 border border-rose-300 rounded p-3 flex gap-2 items-start" data-testid="stale-banner">
            <AlertTriangle className="w-4 h-4 text-rose-700 shrink-0 mt-0.5" />
            <div className="text-xs">
              <div className="font-semibold text-rose-800">Buyer originals have changed since extraction</div>
              <div className="text-rose-700">Re-run extraction before generating a supplier document. Stale source ids: {staleIds.join(", ")}.</div>
            </div>
          </div>
        ) : null}

        <Card className="p-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">Extraction status</h3>
            <span className="text-[10px] text-slate-500">
              engine: {ex?.engine || "—"} v{ex?.engine_version || "—"}
              {ex?.extracted_at ? ` · ${ex.extracted_at.slice(0,16).replace('T',' ')}` : ""}
            </span>
          </div>
          {!ex ? <p className="text-xs text-slate-500 mt-2">No extraction has run on this RFQ yet.</p> : (
            <div className="mt-2 grid grid-cols-4 gap-2 text-xs">
              <div><div className="text-slate-500">Spec version</div><div className="font-semibold">v{rfq?.supplier_spec_version || 0}</div></div>
              <div><div className="text-slate-500">Overall conf.</div><div className="font-semibold">{((ex.overall_confidence || 0) * 100).toFixed(0)}%</div></div>
              <div><div className="text-slate-500">Warnings</div><div className="font-semibold">{(ex.warnings || []).length}</div></div>
              <div><div className="text-slate-500">Unresolved</div><div className={`font-semibold ${requiredKeys.length && !allResolved ? "text-rose-700" : "text-emerald-700"}`}>{requiredKeys.length}{requiredKeys.length ? (allResolved ? " (all reviewed)" : " (open)") : ""}</div></div>
            </div>
          )}
        </Card>

        {ex?.warnings?.length ? (
          <Card className="p-3" data-testid="warnings-card">
            <h3 className="font-semibold text-sm mb-1">Warnings</h3>
            <ul className="text-xs space-y-1">
              {ex.warnings.map((w, i) => <li key={i} className="flex gap-1"><AlertTriangle className="w-3 h-3 text-amber-600 shrink-0 mt-0.5" /><span>{w}</span></li>)}
            </ul>
          </Card>
        ) : null}

        {ex?.redaction_ledger?.length ? (
          <Card className="p-3" data-testid="redaction-ledger">
            <h3 className="font-semibold text-sm mb-1">Redaction ledger <span className="text-[10px] text-slate-500 font-normal">(kind + location only — confidential values are never shown here)</span></h3>
            <ul className="text-xs space-y-1 max-h-40 overflow-auto">
              {ex.redaction_ledger.map((e, i) => (
                <li key={i} className="grid grid-cols-3 gap-2">
                  <span className="font-mono text-rose-700 uppercase">{e.kind}</span>
                  <SourceRef ref={e.source_ref} />
                  <span className="font-mono text-slate-400 truncate">#{e.match_hash}</span>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}

        {requiredKeys.length ? (
          <Card className="p-3" data-testid="unresolved-list">
            <h3 className="font-semibold text-sm mb-1">Unresolved / review-required</h3>
            <ul className="text-xs space-y-2">
              {requiredKeys.map(k => (
                <li key={k} className={`p-2 border rounded ${["accept","edit"].includes(reviews[k]?.action) ? "border-emerald-300 bg-emerald-50" : "border-rose-300 bg-rose-50"}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono">{k}</span>
                    {reviews[k]?.action ? <span className="text-[10px] font-semibold">{reviews[k].action}</span> : <span className="text-[10px] text-rose-700 font-semibold">OPEN</span>}
                  </div>
                  <ReviewControls path={k} review={reviews[k]} onReview={postReview} canReview={canReview} canEdit={canReviewEdit} />
                </li>
              ))}
            </ul>
          </Card>
        ) : null}

        {spec?.line_items?.length ? (
          <Card className="p-3" data-testid="line-items-card">
            <h3 className="font-semibold text-sm mb-2">Line items ({spec.line_items.length})</h3>
            <div className="space-y-3">
              {spec.line_items.map((li, idx) => {
                const liPath = `line_items[${idx}]`;
                const isOcr = li?.source_ref?.via_ocr === true;
                return (
                  <div key={idx} className="border rounded p-2" data-testid={`li-${idx}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs">
                        <div className="font-semibold">{li.title || `(untitled item ${idx + 1})`}</div>
                        <SourceRef ref={li.source_ref} />
                      </div>
                      {isOcr ? <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 rounded"><ScanLine className="w-3 h-3 inline mr-1" />OCR — verify</span> : null}
                    </div>
                    {li.description ? <p className="text-xs mt-1 whitespace-pre-wrap max-h-28 overflow-auto">{li.description}</p> : null}
                    <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                      <div><span className="text-slate-500">Qty:</span> {li.quantity ?? "—"}</div>
                      <div><span className="text-slate-500">UOM:</span> {li.uom || "—"}</div>
                    </div>
                    {["dimensions","tolerances","part_numbers","materials","technical_specs"].map(group => (
                      (li[group]?.length ? (
                        <div key={group} className="mt-2">
                          <div className="text-[10px] uppercase text-slate-500">{group.replace("_"," ")}</div>
                          <ul className="text-xs space-y-1">
                            {li[group].map((a, i) => {
                              const path = `${liPath}.${group}[${i}]`;
                              return (
                                <li key={i} className="flex flex-wrap items-center gap-2 border-l-2 border-slate-200 pl-2">
                                  <span className="font-mono">{a.key || a.axis || a.feature || a.name || a.system || "value"}: {a.value || a.class_or_value || a.name || "—"}{a.unit ? ` ${a.unit}` : ""}</span>
                                  <ConfChip value={a.confidence} viaOcr={a?.source_ref?.via_ocr} />
                                  <SourceRef ref={a.source_ref} />
                                  <ReviewControls path={path} review={reviews[path]} onReview={postReview} canReview={canReview} canEdit={canReviewEdit} />
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ) : null)
                    ))}
                  </div>
                );
              })}
            </div>
          </Card>
        ) : null}

        <Card className="p-3">
          <h3 className="font-semibold text-sm mb-1">Generate Caprocure&trade; supplier document</h3>
          <p className="text-xs text-slate-500 mb-2">
            Gated on: <span className="font-mono">product_title</span> being present AND every unresolved item reviewed (accept or edit). Regeneration always re-drafts the supplier package.
          </p>
          <Button size="sm" disabled={!canGenerate || !canGenerateDoc} data-testid="generate-btn"
            onClick={async () => {
              try {
                const r = await api(`/rfqs/${rfq.id}/generate-supplier-doc`, { method: "POST", body: {} });
                toast.success(`Generated file ${r.file_id.slice(0,8)} · sha256 ${r.sha256.slice(0,10)}…`);
                onRefresh && onRefresh();
              } catch (e) { toast.error(e.message || "Generation refused"); }
            }}>
            {!canGenerateDoc ? "No spec.generate permission" : (canGenerate ? "Generate" : requiredKeys.length ? `${requiredKeys.filter(k => !["accept","edit"].includes(reviews[k]?.action)).length} unresolved` : "product_title required")}
          </Button>
        </Card>
      </div>
    </div>
  );
}
